import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AdSlot from "@/components/AdSlot";
import useSeo from "@/lib/useSeo";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowRight, BookOpen, Zap, Languages, Shield } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1578662996442-48f60103fc96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBjaW5lbWF0aWMlMjB0ZXh0dXJlfGVufDB8fHx8MTc4NjU2MTUzN3ww&ixlib=rb-4.1.0&q=85";

const fade = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.7 } } };

function Section({ children, className = "" }) {
  return (
    <motion.section variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
      className={`max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 ${className}`}>
      {children}
    </motion.section>
  );
}

export default function Landing() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [modules, setModules] = useState([]);

  useSeo(
    `AI Lernwerk — ${t("hero.title1")} ${t("hero.title2")} ${t("hero.title3")}`,
    t("hero.sub")
  );

  useEffect(() => {
    api.get(`/modules?lang=${lang}`).then((r) => setModules(r.data)).catch(() => {});
  }, [lang]);

  const go = () => nav(user && user !== false ? "/dashboard" : "/register");

  const openModule = (moduleId) => {
    nav(`/curriculum#${moduleId}`);
  };

  const benefits = [
    { icon: BookOpen, t: t("benefit.1t"), b: t("benefit.1b") },
    { icon: Languages, t: t("benefit.2t"), b: t("benefit.2b") },
    { icon: Zap, t: t("benefit.3t"), b: t("benefit.3b") },
    { icon: Shield, t: t("benefit.4t"), b: t("benefit.4b") },
  ];

  const faqs = {
    de: [
      ["Ist AI Lernwerk kostenlos?", "Ja. Der Zugang zum Kurs ist in der aktuellen Version kostenlos."],
      ["Brauche ich Vorkenntnisse?", "Nein. Wir beginnen bei den Grundlagen und führen dich bis zum Power-User."],
      ["In welchen Sprachen ist der Kurs?", "Deutsch, Rumänisch und Englisch — Oberfläche und Inhalte."],
      ["Wird mein Fortschritt gespeichert?", "Ja, pro Konto und auf jedem Gerät verfügbar."],
    ],
    ro: [
      ["AI Lernwerk este gratuit?", "Da. Accesul la curs este gratuit în versiunea actuală."],
      ["Am nevoie de cunoștințe anterioare?", "Nu. Începem de la fundamente și te ducem până la power-user."],
      ["În ce limbi este cursul?", "Germană, română și engleză — interfață și conținut."],
      ["Se salvează progresul meu?", "Da, pe cont și disponibil pe orice dispozitiv."],
    ],
    en: [
      ["Is AI Lernwerk free?", "Yes. Course access is free in the current version."],
      ["Do I need prior knowledge?", "No. We start at the fundamentals and take you to power user."],
      ["Which languages is the course in?", "German, Romanian and English — interface and content."],
      ["Is my progress saved?", "Yes, per account and available on any device."],
    ],
  };

  return (
    <div className="min-h-screen bg-void">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-void via-void/70 to-void/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <p className="text-gold text-sm tracking-[0.4em] uppercase mb-8">AI Lernwerk</p>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] text-ink mb-8">
              <span className="block">{t("hero.title1")}</span>
              <span className="block">{t("hero.title2")}</span>
              <span className="block text-gold">{t("hero.title3")}</span>
            </h1>
            <p className="text-ink-soft text-lg md:text-xl max-w-2xl leading-relaxed mb-10">{t("hero.sub")}</p>
            <div className="flex flex-wrap gap-4">
              <button onClick={go} data-testid="hero-cta"
                className="bg-gold text-void px-8 py-4 font-medium hover:bg-gold-hover transition-colors flex items-center gap-2">
                {t("hero.cta")} <ArrowRight size={18} strokeWidth={1.5} />
              </button>
              <Link to="/curriculum" data-testid="hero-cta2"
                className="border border-ink/20 text-ink px-8 py-4 hover:border-gold hover:text-gold transition-colors">
                {t("hero.cta2")}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHAT YOU LEARN */}
      <Section>
        <p className="text-gold text-sm tracking-[0.3em] uppercase mb-4">{t("landing.learn.title")}</p>
        <h2 className="font-serif text-4xl md:text-5xl text-ink max-w-3xl leading-tight mb-6">{t("landing.learn.sub")}</h2>
      </Section>

      {/* CATEGORIES / MODULES */}
      <Section className="!py-16">
        <p className="text-gold text-sm tracking-[0.3em] uppercase mb-10">{t("landing.categories.title")}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/5 border border-ink/5" data-testid="landing-modules">
          {modules.map((m, i) => (
            <button key={m.module_id} onClick={() => openModule(m.module_id)}
              data-testid={`landing-module-${m.module_id}`}
              className="text-left bg-void p-8 hover:bg-graphite transition-colors cursor-pointer group">
              <span className="font-mono text-xs text-gold-muted">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="font-serif text-2xl text-ink mt-3 mb-2 leading-snug group-hover:text-gold transition-colors">{m.title}</h3>
              <p className="text-ink-muted text-sm">{m.description}</p>
            </button>
          ))}
        </div>
      </Section>

      {/* WHY AI MATTERS */}
      <Section>
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-gold text-sm tracking-[0.3em] uppercase mb-4">{t("landing.why.title")}</p>
            <p className="font-serif text-3xl md:text-4xl text-ink leading-snug">{t("landing.why.body")}</p>
          </div>
          <div>
            <p className="text-gold text-sm tracking-[0.3em] uppercase mb-4">{t("landing.progress.title")}</p>
            <p className="text-ink-soft text-lg leading-relaxed">{t("landing.progress.body")}</p>
          </div>
        </div>
      </Section>

      {/* Dormant landing ad slot */}
      <div className="max-w-7xl mx-auto px-6 md:px-12"><AdSlot slot="landing" /></div>

      {/* BENEFITS */}
      <Section>
        <p className="text-gold text-sm tracking-[0.3em] uppercase mb-10">{t("landing.benefits.title")}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefits.map((b, i) => (
            <div key={i} className="border-t border-gold/30 pt-6" data-testid={`benefit-${i}`}>
              <b.icon className="text-gold mb-4" size={28} strokeWidth={1.5} />
              <h3 className="font-serif text-xl text-ink mb-2">{b.t}</h3>
              <p className="text-ink-muted text-sm leading-relaxed">{b.b}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section className="!max-w-3xl">
        <p className="text-gold text-sm tracking-[0.3em] uppercase mb-8">{t("landing.faq.title")}</p>
        <Accordion type="single" collapsible className="w-full" data-testid="faq">
          {(faqs[lang] || faqs.en).map(([q, a], i) => (
            <AccordionItem key={i} value={`q${i}`} className="border-ink/10">
              <AccordionTrigger className="text-ink font-serif text-xl hover:text-gold hover:no-underline text-left">{q}</AccordionTrigger>
              <AccordionContent className="text-ink-soft leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>

      {/* FINAL CTA */}
      <Section className="text-center">
        <h2 className="font-serif text-5xl md:text-6xl text-ink mb-4">{t("landing.finalcta.title")}</h2>
        <p className="text-ink-soft text-lg mb-10">{t("landing.finalcta.sub")}</p>
        <button onClick={go} data-testid="final-cta"
          className="bg-gold text-void px-10 py-4 font-medium hover:bg-gold-hover transition-colors inline-flex items-center gap-2">
          {t("hero.cta")} <ArrowRight size={18} strokeWidth={1.5} />
        </button>
      </Section>

      <Footer />
    </div>
  );
}
